import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue } from "recoil";
import { evaluationAtom } from "@_recoil/_state";
import { authService } from "@unisearch/auth";
import { swalError, getGeoInfo } from "@unisearch/helpers";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "next/link";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { FormControlLabel } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import styles from "./evaluation_signup.module.scss";
import ReCAPTCHA from "react-google-recaptcha";
import Cookies from "js-cookie";

export const EvaluationSignup = forwardRef((props, ref) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const [defaultCountryCode, setDefaultCountryCode] = useState();
  useEffect(() => {
    setTimeout(() => {
      if (Cookies.get("default_country")) {
        setDefaultCountryCode(Cookies.get("default_country"));
      }
    }, 1500);
  }, [defaultCountryCode]);

  useImperativeHandle(ref, () => ({
    formSubmit() {
      document.getElementById("signupBtn").click();
    },
  }));

  //evaluation state
  const {
    countryPreference,
    courseLevelId,
    specializationId,
    departmentIds,
    courseIntake,
    courseExam,
    highestEducation,
    objectiveId,
  } = useRecoilValue(evaluationAtom);

  const [{ is_loading }, setEvaluation] = useRecoilState(evaluationAtom);

  // form validation rules
  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is Required"),
    email: Yup.string()
      .email("Must be a valid email")
      .max(255)
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Must be more than or equal 6 characters")
      .required("Password is required")
      .matches(
        /^(?=.*[0-9])(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{6,}$/,
        // /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/,
        "Must Contain 6 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
      ),
    passwordConfirmation: Yup.string().oneOf(
      [Yup.ref("password"), null],
      "Passwords must match"
    ),
    phone: Yup.string().matches(phoneRegExp, "Phone number is not valid"),
    tandc: Yup.bool().oneOf([true], "Terms & Conditions is required"),
    recaptchaToken: Yup.string()
      .required("reCAPTCHA is required")
      .typeError("reCAPTCHA is required"),
  });
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      tandc: false,
    },
    mode: "onChange",
  };

  // get functions to build form with useForm() hook
  const { register, handleSubmit, setError, formState, setValue } = useForm(
    formOptions
  );
  const { errors } = formState;

  //form submit
  function onSubmit({
    firstName,
    middleName,
    lastName,
    email,
    mobile,
    countryCode,
    password,
    tandc,
    recaptchaToken,
  }) {
    setEvaluation((prev) => ({ ...prev, is_loading: true }));
    let highestEdu = { ...highestEducation };
    delete highestEdu.slug;

    const profileEvaluation = {
      countryPreference,
      courseLevelId,
      specializationId,
      departmentIds,
      courseIntake,
      courseExam,
      highestEducation: highestEdu,
      objectiveId,
    };

    const payload = {
      userTypeSlug: "student",
      firstName,
      middleName,
      lastName,
      email,
      mobile,
      password,
      countryCode,
      profileEvaluation,
      recaptchaToken,
    };

    return authService
      .register(payload)
      .then((data) => {
        const returnUrl = `/email-verification?email=${data}&type=${"student"}`;
        router.push(returnUrl);
        setEvaluation((prev) => ({ ...prev, is_loading: false }));
      })
      .catch((errors) => {
        swalError(errors, "Sorry!");
        setEvaluation((prev) => ({ ...prev, is_loading: false }));
      });
  }

  return (
    <>
      <div className={styles.title_wrapper}>
        <div className={styles.title}>
          Sign Up to View Your Profile Evaluation Report
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.form_wrapper}>
          <form onSubmit={handleSubmit(onSubmit)} id={`signupForm`}>
            <Grid
              container
              rowSpacing={{ xs: 2, sm: 2, md: 3 }}
              columnSpacing={{ xs: 0, sm: 2, md: 3 }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <div className={styles.form_group}>
                  <label>First Name</label>
                  <input
                    id="firstName"
                    name="firstName"
                    placeholder="First Name"
                    type="text"
                    {...register("firstName")}
                    className={`${errors.firstName ? styles.error : ""}`}
                  />
                  <div className={styles.error_msg}>
                    {errors.firstName?.message}
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <div className={styles.form_group}>
                  <label>Middle Name</label>
                  <input
                    id="middleName"
                    name="middleName"
                    placeholder="Middle Name"
                    type="text"
                    {...register("middleName")}
                    className={`${errors.middleName ? styles.error : ""}`}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <div className={styles.form_group}>
                  <label>Last Name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    placeholder="Last Name"
                    type="text"
                    {...register("lastName")}
                    className={`${errors.lastName ? styles.error : ""}`}
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <div className={styles.form_group}>
                  <label>Email Address</label>
                  <input
                    placeholder="Email Address"
                    name="email"
                    type="email"
                    {...register("email")}
                    className={`${errors.email ? styles.error : ""}`}
                  />
                  <div className={styles.error_msg}>
                    {errors.email?.message}
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <div className={styles.form_group}>
                  <label>Mobile Number</label>
                  <PhoneInput
                    country={defaultCountryCode ?? "a"}
                    onChange={(_value, country, _event, formattedValue) => {
                      setValue("mobile", formattedValue);
                      setValue("countryCode", country?.countryCode);
                    }}
                    name={"mobile"}
                    // {...register("mobile")}
                    inputClass={`${errors.mobile ? styles.error : ""}`}
                  />
                  <div className={styles.error_msg}>
                    {errors.mobile?.message}
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <div className={styles.form_group}>
                  <label>Password</label>
                  <input
                    placeholder="Password"
                    name="password"
                    type="password"
                    {...register("password")}
                    className={`${errors.password ? styles.error : ""}`}
                  />
                  <div className={styles.error_msg}>
                    {errors.password?.message}
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <div className={styles.form_group}>
                  <label>Confirm Password</label>
                  <input
                    placeholder="Confirm Password"
                    name="passwordConfirmation"
                    type="password"
                    {...register("passwordConfirmation")}
                    className={`${
                      errors.passwordConfirmation ? styles.error : ""
                    }`}
                  />

                  <div className={styles.error_msg}>
                    {errors.passwordConfirmation?.message}
                  </div>
                </div>
              </Grid>
            </Grid>

            <div className={styles.terms_wrapper}>
              <FormControlLabel
                control={
                  <Checkbox name="tandc" value={true} {...register("tandc")} />
                }
                label="I agree to"
              />

              <Link legacyBehavior href="/terms-and-conditions">
                <a className={styles.term_link}>Terms & Conditions</a>
              </Link>
              <div className={styles.error_msg}>{errors.tandc?.message}</div>
            </div>

            <ReCAPTCHA
              sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY}
              onChange={(e) => {
                setValue("recaptchaToken", e);
                setError("recaptchaToken", {
                  type: "manual",
                  message: undefined,
                });
              }}
            />
            <div className={styles.error_msg}>
              {errors.recaptchaToken?.message}
            </div>
            <button
              className="btn btn-secondary btn-bg"
              type="submit"
              id="signupBtn"
              style={{ display: "none" }}
              disabled={is_loading}
            >
              {is_loading && <CircularProgress size={20} />} submit
            </button>
          </form>
        </div>
      </div>
    </>
  );
});

export default EvaluationSignup;
